import React, { useState, useMemo, ReactNode, MouseEventHandler } from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultTooltip, { DefaultTooltipProps } from 'components/Utils/DefaultTooltip';
import icons from './icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { CSSPropertiesWithVars } from '../../../services/TypeUtils';

const alts: Record<string, string> = {
  'message-captions': 'Chat Shortcuts',
  'chart-line': 'Diagnostics',
  'users-medical': 'Practitioners',
  'user-doctor': 'Referrals',
  'circle-check': 'Tasks',
  prescription: 'Prescriptions',
  'vial-virus': 'Immunizations',
  'head-side-cough': 'Allergies',
  dumbbell: 'Lifestyle Factors',
  wallet: 'Billing',
  users: 'Patients',
  'calendar-clock': 'Appointments',
  house: 'Home',
  'box-archive': 'Archive',
  xmark: 'Close',
  trash: 'Remove',
  'inbox-full': 'Patient Communication',
  family: 'Linked Accounts',
  fax: 'Faxes',
  envelope: 'Emails',
  'files-medical': 'Documents',
  'house-blank': 'Home',
  'book-medical': 'Medical Issues',
  shuffle: 'Linked Accounts',
  pencil: 'Edit',
  star: 'Favorites',
  'list-ul': 'Timeline',
  'memo-pad': 'Encounter Note',
  'user-nurse-hair-long': 'Staff',
  plus: 'Add',
};

export type IconSrc = string | [IconPrefix, IconName];

export type IconProps = {
  id?: string;
  className?: string;
  src?: IconSrc;
  fontAwesome?: IconName;
  fontAwesomeStyle?: IconPrefix;
  showTooltip?: boolean;
  alt?: string;
  height?: string;
  width?: string;
  iconStyle?: CSSPropertiesWithVars;
  style?: CSSPropertiesWithVars;
  children?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>, src?: string) => void;
  onClickCapture?: (event: React.MouseEvent<HTMLElement, MouseEvent>, src?: string) => void;

  //state
  active?: boolean;
  activeSrc?: string;
  loading?: boolean;
  disabled?: boolean;
  tooltipPlace?: DefaultTooltipProps['placement'];
};

const Icon = (props: IconProps) => {
  let {
    loading = false,
    id,
    src,
    activeSrc,
    active = false,
    fontAwesome,
    fontAwesomeStyle = 'far',
    showTooltip = false,
    tooltipPlace = 'top',
    alt,
    height,
    width,
    iconStyle = {},
    children,
    disabled = false,
  } = props;

  if (!width && !height) width = '15px';

  if (Array.isArray(src)) {
    fontAwesomeStyle = src[0];
    src = src[1];
  }

  const style = useMemo(() => {
    return getStyle(props.style, width, height);
  }, [props.style, width, height]);

  const clickable = props.onClick !== undefined || props.onClickCapture !== undefined;

  const className = useMemo(() => {
    return getClassName(props.className, loading, clickable, active, disabled);
  }, [props.className, loading, clickable, active, disabled]);

  const { description, iconId } = useMemo(() => {
    let description = getAlt((src as string) || fontAwesome, alt);
    let iconId = id;
    if (description && !id) iconId = description.replace(/ /g, '_') + '-icon';
    return { description, iconId };
  }, [src, fontAwesome, alt, id]);

  const finalSrc = useMemo(() => {
    return getImage(src as string, activeSrc, active);
  }, [src, activeSrc, active]);

  const onClick: MouseEventHandler<HTMLElement> = (event) => {
    const propsOnClick = props.onClick ?? props.onClickCapture;
    if (disabled || loading) return;
    if (propsOnClick) propsOnClick(event, (props.src as string) || props.fontAwesome);
  };

  if (!finalSrc && !fontAwesome) fontAwesome = src as IconName;

  return (
    <div className={className} style={style}>
      {loading && (
        <ProgressMessage
          show={loading}
          className="icon-loading"
          size={style.width || style.height}
        />
      )}
      {!loading && showTooltip && (
        <DefaultTooltip
          arrow={true}
          placement={tooltipPlace}
          title={description}
          open={!showTooltip ? false : undefined}
        >
          <div style={fontAwesome ? undefined : { width: style.width, height: style.height }}>
            <IconImage
              id={iconId}
              description={description}
              finalSrc={finalSrc}
              fontAwesomeStyle={fontAwesomeStyle}
              fontAwesome={fontAwesome!}
              style={style}
              iconStyle={iconStyle}
              onClick={onClick}
            />
            {children}
          </div>
        </DefaultTooltip>
      )}
      {!loading && !showTooltip && (
        <div style={fontAwesome ? undefined : { width: style.width, height: style.height }}>
          <IconImage
            id={iconId}
            description={description}
            finalSrc={finalSrc}
            fontAwesomeStyle={fontAwesomeStyle}
            fontAwesome={fontAwesome!}
            style={style}
            iconStyle={iconStyle}
            onClick={props.onClick && onClick}
            onClickCapture={props.onClickCapture && onClick}
          />
          {children}
        </div>
      )}
    </div>
  );
};

type IconImageFA = {
  fontAwesome: IconName;
  fontAwesomeStyle: IconPrefix;
};

type IconImageSrc = {
  finalSrc: string;
};

type IconImageProps = (IconImageFA | IconImageSrc) & {
  id?: string;
  style?: CSSPropertiesWithVars;
  description?: string;
  iconStyle?: CSSPropertiesWithVars;
  onClick?: (ev: React.MouseEvent<HTMLElement, MouseEvent>, src?: string) => void;
  onClickCapture?: (ev: React.MouseEvent<HTMLElement, MouseEvent>, src?: string) => void;
};

const IconImage = ({
  id,
  style,
  iconStyle,
  description,
  onClick,
  onClickCapture,
  ...props
}: IconImageProps) => {
  return 'fontAwesome' in props && props.fontAwesome ? (
    <FontAwesomeIcon
      id={id}
      style={{ width: style?.width, height: style?.height, ...iconStyle }}
      //@ts-expect-error
      alt={description}
      icon={[props.fontAwesomeStyle, props.fontAwesome]}
      onClick={onClick as any}
      onClickCapture={onClickCapture as any}
    />
  ) : (
    <img
      id={id}
      alt={description}
      src={'finalSrc' in props ? props.finalSrc : undefined}
      style={{ width: style?.width, height: style?.height }}
      onClick={onClick}
      onClickCapture={onClickCapture}
    />
  );
};

function getClassName(
  className: string | undefined,
  loading: boolean,
  clickable: boolean,
  active: boolean,
  disabled: boolean,
) {
  return `icon${className ? ` ${className}` : ''}${clickable ? ' clickable' : ''}${
    disabled ? ' disabled' : ''
  }${active || loading ? ' blue' : ''}`;
}

function getStyle(style: CSSPropertiesWithVars = {}, width?: string, height?: string) {
  return width || height
    ? {
        ...style,
        minWidth: width,
        width,
        minHeight: height,
        height,
        lineHeight: height,
      }
    : style;
}

function getImage(src = '', activeSrc: string | undefined, active: boolean) {
  const srcStr = (active ? activeSrc || `${src}-blue` : src).replace(/-/g, '_');
  return (icons as Record<string, string>)[srcStr];
}

function getAlt(src = '', alt: string | undefined) {
  return (
    alt ||
    alts[src] ||
    src
      .replace(/-/g, ' ')
      .replace('icon', '')
      .trim()
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

Icon.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  activeSrc: PropTypes.string,
  alt: PropTypes.string,
  extension: PropTypes.string,
  className: PropTypes.string,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
  style: PropTypes.object,
  showTooltip: PropTypes.bool,
  tooltipPlace: PropTypes.string,
};

export default Icon;
